/**
 * Central application state (zustand). Composes the pure orchestration
 * functions in `src/engine/orchestrator.ts` with the async bits that
 * can't live there: the bot Web Worker client, IndexedDB persistence,
 * and the auto-advance-to-next-hand timer.
 */
import { create } from "zustand";
import {
  mulberry32,
  type Action,
  type HandConfig,
  type HandState,
  type MatchState,
  type PlayerIndex,
  type Rng,
} from "@joker-holdem/core";
import { botViewFor } from "@joker-holdem/bot";
import {
  applyGameAction,
  beginMatch,
  completeHand,
  dealNextHand,
} from "../engine/orchestrator.js";
import { BotClient } from "../engine/botClient.js";
import { saveHand, saveMatchResult } from "../history/db.js";
import type { RecentHandSummary } from "../types.js";

export const HERO: PlayerIndex = 0;
export const BOT: PlayerIndex = 1;

/**
 * Only a preflop fold auto-advances now -- it skips the result UI entirely
 * (docs/UI_REFERENCE.md "フィードバック反映" 2026-07-17 #2 例外), so a short
 * tempo-preserving delay is enough. Every other ending shows the result
 * banner and waits for the player to tap "次のハンド" (the old blanket
 * 1.2s auto-advance timer is gone).
 */
const PREFLOP_FOLD_AUTO_ADVANCE_MS = 300;

function seedFromUrl(): number | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("seed");
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// Non-reactive engine handles: they don't need to trigger re-renders and
// aren't meaningfully comparable/serializable, so they live outside the
// zustand-managed state slice.
let dealRng: Rng | null = null;
let botClient: BotClient | null = null;

export interface GameStoreState {
  screen: "start" | "play" | "history";
  config: HandConfig | null;
  anteEnabled: boolean;
  matchId: string | null;
  seed: number | null;
  match: MatchState | null;
  hand: HandState | null;
  botThinking: boolean;
  /** Hand just finished; waiting for the auto-advance timer (or a tap) to deal the next one. */
  awaitingAdvance: boolean;
  recentHands: RecentHandSummary[];
  matchOver: boolean;
  historyDetailId: number | null;

  startMatch: (anteEnabled: boolean) => void;
  heroAct: (action: Action) => void;
  advanceToNextHand: () => void;
  goHistory: () => void;
  goPlay: () => void;
  goStart: () => void;
  openHistoryDetail: (id: number) => void;
  closeHistoryDetail: () => void;
  rematch: () => void;
}

export const useGameStore = create<GameStoreState>((set, get) => {
  function applyActionInternal(action: Action) {
    const state = get();
    if (!state.hand) return;
    const nextHand = applyGameAction(state.hand, action);
    set({ hand: nextHand, botThinking: false });
    if (nextHand.result) {
      void finishHand();
    } else {
      void maybeTriggerBot();
    }
  }

  async function maybeTriggerBot() {
    const state = get();
    if (!state.hand || state.hand.result || !botClient) return;
    if (state.hand.toAct !== BOT) return;
    const handAtRequestTime = state.hand;
    set({ botThinking: true });
    const view = botViewFor(state.hand, BOT);
    const action = await botClient.decide(view);
    // If the hand moved on while we were "thinking" (e.g. a rematch
    // reset state), discard this stale result instead of applying it.
    if (get().hand !== handAtRequestTime) return;
    applyActionInternal(action);
  }

  function finishHand() {
    const state = get();
    if (!state.hand || !state.match || !state.matchId || !state.hand.result) return;
    const { record, match: nextMatch, matchOver } = completeHand(state.match, state.hand);
    const summaryKey = `${state.matchId}-${nextMatch.handNumber}`;

    // Game flow FIRST, persistence AFTER (fire-and-forget). Awaiting the
    // IndexedDB write before updating state froze the whole game (next-hand
    // button, post-fold advance) whenever storage hung -- e.g. iOS Safari
    // with another tab blocking a version upgrade (2026-07-17 bug report).
    const summary: RecentHandSummary = {
      key: summaryKey,
      id: null,
      holeCards: record.holeCards[0],
      netBb: record.netResult[0] / 2,
    };
    const recentHands = [summary, ...get().recentHands].slice(0, 3);
    set({ match: nextMatch, recentHands, matchOver, awaitingAdvance: !matchOver });

    const isPreflopFold = state.hand.result.endedBy === "fold" && state.hand.street === "preflop";
    if (!matchOver && isPreflopFold) {
      setTimeout(() => {
        if (get().awaitingAdvance) get().advanceToNextHand();
      }, PREFLOP_FOLD_AUTO_ADVANCE_MS);
    }

    // Best-effort persistence; on success, patch the summary's id so the
    // recent-hands bar can open the detail modal.
    void saveHand(state.matchId, nextMatch.handNumber, record)
      .then((id) => {
        if (id === null) return;
        set({
          recentHands: get().recentHands.map((h) => (h.key === summaryKey ? { ...h, id } : h)),
        });
      })
      .catch((err) => console.warn("[joker-holdem] ハンド履歴の保存に失敗しました。", err));

    // Match-result persistence (2026-07-17 フィードバック #2): recorded the
    // moment the match-ending hand completes, not when the player taps a
    // button on the result screen -- so it can't be skipped by navigating
    // away before tapping anything.
    if (matchOver) {
      const winnerSeat: PlayerIndex = nextMatch.stacks[0] >= nextMatch.stacks[1] ? 0 : 1;
      void saveMatchResult({
        matchId: state.matchId,
        endedAt: Date.now(),
        winnerSeat,
        handCount: nextMatch.handNumber,
        anteEnabled: state.anteEnabled,
        finalStacks: nextMatch.stacks,
      }).catch((err) => console.warn("[joker-holdem] 戦績の保存に失敗しました。", err));
    }
  }

  function startMatchInternal(anteEnabled: boolean) {
    const config: HandConfig = {
      startingStackChips: 100,
      sbChips: 1,
      bbChips: 2,
      antePerPlayerChips: anteEnabled ? 1 : 0,
    };
    const seed = seedFromUrl() ?? Date.now();
    dealRng = mulberry32(seed);
    botClient?.dispose();
    botClient = new BotClient(seed + 1);
    const matchId = `m${seed}`;
    const { match, hand } = beginMatch(config, dealRng, 0);

    set({
      screen: "play",
      config,
      anteEnabled,
      matchId,
      seed,
      match,
      hand,
      botThinking: false,
      awaitingAdvance: false,
      recentHands: [],
      matchOver: false,
      historyDetailId: null,
    });
    void maybeTriggerBot();
  }

  return {
    screen: "start",
    config: null,
    anteEnabled: true,
    matchId: null,
    seed: null,
    match: null,
    hand: null,
    botThinking: false,
    awaitingAdvance: false,
    recentHands: [],
    matchOver: false,
    historyDetailId: null,

    startMatch: startMatchInternal,

    heroAct: (action) => {
      const state = get();
      if (!state.hand || state.hand.result || state.hand.toAct !== HERO) return;
      applyActionInternal(action);
    },

    advanceToNextHand: () => {
      const state = get();
      if (!state.match || state.matchOver || !state.awaitingAdvance || !dealRng) return;
      const nextHand = dealNextHand(state.match, dealRng);
      set({ hand: nextHand, awaitingAdvance: false });
      void maybeTriggerBot();
    },

    goHistory: () => set({ screen: "history" }),
    goPlay: () => set({ screen: "play" }),
    goStart: () => set({ screen: "start" }),
    openHistoryDetail: (id) => set({ historyDetailId: id }),
    closeHistoryDetail: () => set({ historyDetailId: null }),

    rematch: () => {
      const anteEnabled = get().anteEnabled;
      startMatchInternal(anteEnabled);
    },
  };
});
